import type { Validation } from "@/lib/validation"
import { attemptArraySalvage } from "@/lib/validation/array/attemptSalvage"
import { buildValidationResult } from "@/lib/validation/buildResult"
import { isArray } from "@/lib/validation/isArray"
import { validateElement } from "@/lib/validation/validateElement"

const structure = `array` as const

export const buildArrayValidator = <
  Registry extends Validation.BaseRegistry,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Element
>({
  api,
  expectation,
}: {
  api: Validation.API<Registry>
  expectation: Validation.Expectation<Registry, Element>
}): Validation.Validator<Element, `array`> => {
  const validator: Validation.Validator<Element, `array`> = {
    getFailures: (subject, name) => {
      return validator.validate(subject, name).failures
    },

    getSalvage: (subject, name, attemptSalvage) => {
      return validator.validate(subject, name, attemptSalvage).salvage
    },

    isValid: (subject, name): subject is Element[] => {
      return validator.validate(subject, name).valid
    },

    validate: (subject, name, attemptSalvage = attemptArraySalvage) => {
      expectation
      if (!isArray(subject)) {
        return buildValidationResult({
          failures: [
            {
              element: subject,
              expectation,
              key: `(subject)`,
              name,
              reason: `${name} expected to be an array, but isn't. It has typeof ${typeof subject}`,
              subject,
            },
          ],
          subject,
        })
      }

      const cached = api.privateFunctions.getCachedResult({
        expectation,
        structure,
        subject,
      })

      if (cached) {
        return cached
      }

      const failures: Validation.Failure[] = []
      const validElements: Element[] = []

      subject.forEach((element, index) => {
        const elementFailures = validateElement({
          api,
          element,
          expectation,
          key: index.toString(),
          subject,
          subjectName: name,
        })

        if (elementFailures.length > 0) {
          failures.push(...elementFailures)
        } else {
          validElements.push(element as Element)
        }
      })

      const salvage = attemptSalvage({
        failures,
        name,
        subject,
        validElements,
      })

      const result = buildValidationResult({
        failures,
        salvage,
        subject,
      })

      api.privateFunctions.cacheResult({
        expectation,
        result,
        structure,
      })

      return result
    },
  }

  return validator
}
